<template>
	<!-- prefer using <search> element over role="search" but it's currently not supported in Vue -->
	<form role="search" @submit.prevent="triggerSearch('keyboard')">
		<!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
		<div
			class="tw relative mx-4 mb-7 mt-8 h-10 focus-within:text-black-primary px1408:m-0 px1408:w-80"
			:class="{ 'text-gray-80': isInputEmpty }"
			@keydown="onKeyDown"
		>
			<label :for="INPUT_ID" class="sr-only">Artikel suchen</label>
			<input
				:id="INPUT_ID"
				ref="inputEl"
				role="combobox"
				:aria-controls="TYPE_AHEAD_LISTBOX_ID"
				:aria-expanded="showSearchBox"
				:aria-activedescendant="focusedTypeAheadId"
				aria-describedby="inputDescription"
				type="text"
				class="h-10 w-full scroll-mt-2 rounded-full bg-gray-20 px-11 text-black-primary outline outline-transparent placeholder:text-gray-80 hover:bg-gray-40 focus:bg-gray-40 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-current"
				:maxlength="userInputMaxlength"
				:value="userInput"
				:placeholder="isListening ? 'Was suchen Sie?' : `Suche in: ${departmentCapitalized}`"
				autocomplete="off"
				aria-autocomplete="list"
				@input="onInput($event.target as HTMLInputElement)"
				@keyup.enter="triggerSearch('keyboard')"
				@click="handleInputClick"
				@focus="handleInputFocus"
				@blur="handleInputBlur"
			/>
			<span id="inputDescription" class="sr-only">Die Ergebnisse werden während der Eingabe aktualisiert.</span>
			<button
				type="submit"
				class="absolute left-3.5 top-1/2 grid size-6 -translate-y-1/2 place-content-center"
				aria-label="Suchen"
				:aria-disabled="isInputEmpty"
				@click="triggerSearch('click')"
			>
				<SvgIcon icon-name="magnifying-glass" class="size-5" />
			</button>
			<button
				v-if="isSpeechSupported && isInputEmpty"
				type="button"
				class="absolute right-3.5 top-1/2 grid size-6 -translate-y-1/2 place-content-center rounded-xl"
				:class="isListening ? 'srh__bg-pulse text-white' : ' supportsHover:hover:text-black-primary'"
				:aria-label="isListening ? 'Spracheingabe beenden' : 'Spracheingabe starten'"
				@click="triggerSpeech"
			>
				<SvgIcon icon-name="mic" class="size-5" />
			</button>
			<button
				v-else
				type="button"
				class="absolute right-3.5 top-1/2 size-6 -translate-y-1/2 place-content-center rounded-xl"
				:class="isInputEmpty ? 'hidden' : 'grid'"
				aria-label="Eingabe löschen"
				@click="deleteSearch"
			>
				<SvgIcon icon-name="close-new" class="size-5" />
			</button>
		</div>

		<div
			:id="TYPE_AHEAD_LISTBOX_ID"
			ref="typeAheadContainerEl"
			role="listbox"
			aria-label="Suchvorschläge"
			class="o-scrollbar max-h-[calc(100vh-200px)] overscroll-contain"
			:class="[
				inDrawer
					? 'px-4 pb-4'
					: 'absolute right-0 top-full z-[100] max-h-[calc(100vh-200px)] w-[35.7142857rem] border-t-2 border-t-gray-40 bg-white p-4 text-sm shadow-4',
				{ hidden: !showSearchBox },
			]"
			style="clip-path: inset(0 -10px -10px -10px)"
		>
			<template v-if="isLoading">Loading …</template>
			<TypeAhead v-else />
		</div>

		<div role="status" class="sr-only">
			{{ nrOfDisplayedTypeAheadItems === 0 ? "Keine Suchergebnisse." : "" }}
		</div>
	</form>
</template>

<script lang="ts" setup>
import { ref, useTemplateRef, computed, watch, onMounted, provide } from "vue";
import useSearch, { focusedTypeAheadId, focusedTypeAheadIndex } from "@/SearchApp/composables/use-search";
import useDepartment from "@/SearchApp/composables/use-department";
import { language } from "@ocm/services/ocm-object";
import SvgIcon from "@SharedVueComponents/SvgIcon/SvgIcon.vue";
import { useDebounceFn, useSpeechRecognition } from "@vueuse/core";
import TypeAhead from "./TypeAhead.vue";
import { typeAheadContainerKey } from "@/SearchApp/constants/injectionKeys";
import { skipLinks } from "@ocm/services/services.skipLinks";
import { tr_searchClick } from "@/SearchApp/services/dataLayer";

const props = defineProps<{
	inDrawer?: boolean;
}>();

const INPUT_ID = skipLinks.search.input.slice(1); // #header-search
const TYPE_AHEAD_LISTBOX_ID = "typeAheadListbox";

const { departmentCapitalized } = useDepartment();
const {
	isLoading,
	userInput,
	userInputMaxlength,
	setInputElementRef,
	openSearch,
	triggerSearch,
	deleteSearch,
	onKeyDown,
	onInput,
	isSearchVisible,
	nrOfDisplayedTypeAheadItems,
} = useSearch();

const typeAheadContainerEl = useTemplateRef("typeAheadContainerEl");
provide(typeAheadContainerKey, typeAheadContainerEl);

const inputEl = useTemplateRef("inputEl");

const isFocusOnInput = ref(false);

onMounted(() => {
	setInputElementRef(inputEl.value);
});

const isInputEmpty = computed(() => userInput.value === "");

const showSearchBox = computed(
	() =>
		props.inDrawer ||
		(isSearchVisible.value && isFocusOnInput.value && (isLoading.value || !!nrOfDisplayedTypeAheadItems.value))
);

function handleInputFocus() {
	isFocusOnInput.value = true;
	openSearch();
}

function handleInputBlur() {
	// delay to allow click event on typeAheadItem to be processed first
	window.setTimeout(() => {
		isFocusOnInput.value = false;
		focusedTypeAheadIndex.value = -1;
	}, 200);
}

function handleInputClick() {
	if (!props.inDrawer) tr_searchClick("Field");
}

const _debounceSearch = useDebounceFn(triggerSearch, 600);
const speech = useSpeechRecognition({ lang: language, interimResults: false });
const { isListening, isSupported: isSpeechSupported } = speech;
const triggerSpeech = () => speech.toggle(!isListening.value);
watch(speech.result, (result) => {
	speech.stop();
	userInput.value = result;
	_debounceSearch("voice");
});
</script>

<style>
.srh__bg-pulse {
	animation: bgPulse 1200ms infinite;
}

@keyframes bgPulse {
	0% {
		background-color: rgba(0, 113, 33, 0.6);
	}
	60% {
		background-color: rgba(0, 113, 33, 1);
	}
	100% {
		background-color: rgba(0, 113, 33, 0.6);
	}
}
</style>
